import React, { useState, useEffect, forwardRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, NavLink } from 'react-router-dom';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import { useTheme, styled } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import FormLabel from '@mui/material/FormLabel';
import Grid from '@mui/material/Grid';

import {
    StyledSelect,
    StyledMobileMenuItem,
} from '../../../../components/inputs/styled-secondary-select';
import { getNavigation } from '../../../../selectors/app';
import Colors from '../../../../styles/colors';
import { getModelsList } from '../../../../selectors/cms';
import countAvailableWorkshops from '../../../../utils/workshops';

const ButtonLinkSecondary = forwardRef(
    ({ className = '', ...otherProps }, ref) => (
        <NavLink
            ref={ref}
            className={({ isActive }) => (isActive
                ? `${className} sideNavSupport sideNavActiveSupport`
                : `${className} sideNavSupport`)}
            {...otherProps}
        />
    ),
);

ButtonLinkSecondary.propTypes = { className: PropTypes.string };
ButtonLinkSecondary.defaultProps = { className: '' };

const StyledDot = styled('div')(() => ({
    color: 'white',
    backgroundColor: Colors.grey2,
    height: 30,
    width: 30,
    borderRadius: 30,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: '1.3rem',
    fontWeight: 700,
    marginRight: 15,
}));

const subLinkStyles = {
    display: 'inline-flex',
    alignItems: 'center',
    minHeight: 32,
    fontSize: '1.4rem',
    color: 'black',
    textDecoration: 'none',
    '&[aria-current=page]': { textDecoration: 'underline', textUnderlineOffset: '4px' },
    '&:hover': { textDecoration: 'underline' },
    '&:focus-visible': { outline: `2px solid ${Colors.blue}`, outlineOffset: 3 },
};

const SupportSideNav = () => {
    const type = 'support';
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

    const navigation = useSelector(getNavigation);
    const getAll = useSelector(getModelsList);
    const location = useLocation();
    const [selectedValue, setSelectedValue] = useState('');
    const [selectiveNav, setSelectiveNav] = useState([]);
    useEffect(() => {
        setSelectedValue(location.pathname.split('/')[2] ?? '');
    }, [location]);

    const navigate = useNavigate();
    const handleMobileNavChange = (evt) => {
        navigate(`/support/${String(evt.target.value)}`);
    };

    useEffect(() => {
        setSelectiveNav(navigation[type]?.secondary?.slice(2) ?? []);
    }, [navigation]);

    const subNav = (item) => {
        const count = countAvailableWorkshops(
            getAll('support-steps').find((step) => step.title?.value === item.id)?.workshops,
        );
        return (
            <Stack
                component="nav"
                aria-label={`${item.label} sections`}
                spacing={1}
                sx={{
                    pl: { xs: 0, lg: '50px' }, pb: 3, pt: 1, alignItems: 'flex-start',
                }}
            >
                <Link component={NavLink} to={`/support/${item.path}`} end sx={subLinkStyles}>
                    Instructions &amp; Videos
                </Link>
                <Link component={NavLink} to={`/support/${item.path}/workshops`} sx={subLinkStyles}>
                    Workshops
                    {count > 0 && (
                        <Box
                            component="span"
                            aria-label={`${count} available ${count === 1 ? 'workshop' : 'workshops'}`}
                            sx={{
                                ml: 1.5,
                                px: '6px',
                                minWidth: 18,
                                height: 18,
                                borderRadius: '40px',
                                backgroundColor: Colors.yellow,
                                color: 'black',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '1rem',
                                fontWeight: 600,
                            }}
                        >
                            {count}
                        </Box>
                    )}
                </Link>
            </Stack>
        );
    };

    const desktopNav = () => selectiveNav.map((item, i) => (
        <Box key={`${item.path}-sidenav-desktop`} sx={{ width: '100%' }}>
            <Button to={`/support/${item.path}`} component={ButtonLinkSecondary}>
                <div className="number">{i + 1}</div>
                {item.label}
            </Button>
            {selectedValue === item.path && subNav(item)}
        </Box>
    ));
    const mobileNav = () => {
        const navItems = selectiveNav.map((item, i) => (
            <StyledMobileMenuItem key={item.path} value={item.path}>
                <StyledDot>{i + 1}</StyledDot>
                <span>{item.label}</span>
            </StyledMobileMenuItem>
        ));

        return (
            <div style={{ textAlign: 'left', width: '100%' }}>
                <FormLabel
                    htmlFor="support-step-select"
                    style={{
                        textTransform: 'none',
                        fontSize: '1.6rem',
                        display: 'block',
                        fontWeight: '700',
                        color: 'black',
                    }}
                >
                    Your step:
                </FormLabel>
                <StyledSelect
                    id="support-step-select"
                    inputProps={{ 'aria-label': 'Your step' }}
                    value={selectedValue}
                    onChange={handleMobileNavChange}
                    renderValue={(value) => `${String(value)}. ${selectiveNav.find((item) => item.path === String(value))?.label ?? ''}`}
                    MenuProps={{
                        PaperProps: {
                            style: {
                                borderRadius: 12,
                            },
                        },
                    }}
                >
                    {navItems}
                </StyledSelect>
                {selectiveNav
                    .filter((item) => item.path === selectedValue)
                    .map((item) => (
                        <React.Fragment key={item.path}>{subNav(item)}</React.Fragment>
                    ))}
            </div>
        );
    };
    return (
        <Grid container alignItems="center" style={{ flexGrow: 1 }} component="section">
            <Grid
                item
                sx={{
                    width: !isMobile ? 300 : '100%',
                    display: 'flex',
                    flexGrow: 1,
                    flexDirection: 'column',
                    alignItems: { xs: 'center', lg: 'start' },
                    pr: { xs: 0, lg: 8 },
                }}
            >
                {!isMobile && desktopNav()}
                {isMobile && mobileNav()}
            </Grid>
        </Grid>
    );
};

export default SupportSideNav;
